/**
 * DocumentValidationPage - Page for validating extracted document data
 *
 * Displays extracted fields from AI processing and allows users to:
 * - Review extracted data
 * - Select/deselect which fields to save
 * - Edit field values
 * - Submit validated data to database
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { DocumentValidationHeader } from '../components/validation/DocumentValidationHeader';
import { LineItemsSection } from '../components/validation/LineItemsSection';
import { LoanTermsSection } from '../components/validation/LoanTermsSection';
import { TransactionBasicsSection } from '../components/validation/TransactionBasicsSection';
import { useDocuments, useSubmitValidationSession, useValidationSession } from '../hooks';
import { useValidationStore } from '../store';

export interface DocumentValidationPageProps {
  sessionId?: string;
}

export function DocumentValidationPage({ sessionId: propSessionId }: DocumentValidationPageProps) {
  // TOP OF COMPONENT - This should log immediately when page loads
  console.log('[DocumentValidationPage] COMPONENT LOADED!!!', { propSessionId });

  const { transactionId, documentId } = useParams<{
    transactionId: string;
    documentId: string;
  }>();
  const location = useLocation();
  // Get sessionId and orderId from navigation state (passed from DocumentCard in orders feature)
  const stateSessionId = location.state?.sessionId as string | undefined;
  const stateOrderId = location.state?.orderId as string | undefined;
  const sessionId = propSessionId || stateSessionId; // Priority: prop > navigation state

  console.log('[DocumentValidationPage] Page params:', {
    sessionId,
    propSessionId,
    stateSessionId,
    transactionId,
    documentId,
  });

  const navigate = useNavigate();
  const [isPolling, setIsPolling] = useState(!sessionId);
  const [resolvedSessionId, setResolvedSessionId] = useState(sessionId || null);
  const [pollError, setPollError] = useState<string | null>(null);
  const { toast } = useToast();

  console.log('[DocumentValidationPage] Initial state:', {
    sessionId,
    propSessionId,
    isPolling,
    resolvedSessionId,
    willPollDocuments: !sessionId && !!transactionId,
  });

  const {
    setSession,
    clearSession,
    selectedFields,
    isSubmitting,
    isDirty,
    setIsSubmitting,
    setError,
    selectAllFields,
    deselectAllFields,
  } = useValidationStore();

  // Call the submit mutation hook at component level (not inside callbacks)
  const submitValidationMutation = useSubmitValidationSession();

  // Fetch documents to poll for validation_session_id
  console.log('[DocumentValidationPage] Polling config:', { transactionId, isPolling });

  const { data: documents } = useDocuments(transactionId || '', undefined, {
    enabled: isPolling && !!transactionId,
    refetchInterval: isPolling ? 2000 : false, // Poll every 2 seconds
  });

  console.log('[DocumentValidationPage] Documents query result:', {
    documents,
    isLoading: !documents,
  });

  // Extract validationSessionId from documents when polling
  useEffect(() => {
    console.log('[DocumentValidationPage] Polling effect triggered:', {
      isPolling,
      documentsCount: documents?.length,
      documentId,
    });

    if (!isPolling || !documents || !documentId) {
      console.log('[DocumentValidationPage] Polling effect skipped:', {
        isPolling,
        hasDocuments: !!documents,
        documentId,
      });
      return;
    }

    console.log('[DocumentValidationPage] Polling documents:', {
      documentsCount: documents.length,
      documentId,
      documents: documents.map((d) => ({
        id: d.id,
        documentId: (d as any).documentId,
        validationSessionId: (d as any).validationSessionId,
        processingStatus: (d as any).processingStatus,
      })),
    });

    // API returns documentId, but CDDocument type uses id
    const document = documents.find((doc) => {
      const docId = (doc as any).documentId || doc.id;
      return docId === documentId;
    });

    console.log(
      '[DocumentValidationPage] Found document:',
      document
        ? {
            documentId: (document as any).documentId || document.id,
            validationSessionId: (document as any).validationSessionId,
            processingStatus: (document as any).processingStatus,
          }
        : null,
    );

    if (document?.validationSessionId) {
      // Found the validation session ID - stop polling and use it
      console.log(
        '[DocumentValidationPage] Found validationSessionId:',
        (document as any).validationSessionId,
      );
      setResolvedSessionId((document as any).validationSessionId);
      setIsPolling(false);
      setPollError(null);
    } else if ((document as any)?.processingStatus === 'failed') {
      console.log('[DocumentValidationPage] Document processing failed');
      setIsPolling(false);
      setPollError('Document processing failed. Please try again.');
    } else if ((document as any)?.processingStatus === 'completed') {
      console.log('[DocumentValidationPage] Document completed without validation');
      setIsPolling(false);
      setPollError(
        'Document was processed without validation. This should not happen for CD documents.',
      );
    }
  }, [documents, documentId, isPolling]);

  // Fetch validation session once we have the ID
  const effectiveSessionId = resolvedSessionId || sessionId || '';
  const shouldEnableQuery = !!effectiveSessionId && !isPolling;

  console.log('[DocumentValidationPage] Validation session query config:', {
    effectiveSessionId,
    resolvedSessionId,
    paramSessionId: sessionId,
    isPolling,
    shouldEnableQuery,
  });

  const {
    data: session,
    isLoading,
    error,
    isFetching,
    status,
  } = useValidationSession(effectiveSessionId, {
    enabled: shouldEnableQuery,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache
  });

  // Debug: log when query state changes
  useEffect(() => {
    console.log('[DocumentValidationPage] Query state changed:', {
      isLoading,
      isFetching,
      status,
      error: error?.message,
      sessionData: !!session,
      sessionKeys: session ? Object.keys(session) : [],
      effectiveSessionId,
      shouldEnableQuery,
    });
  }, [isLoading, isFetching, status, error, session, effectiveSessionId, shouldEnableQuery]);

  // Update store when session data loads
  useEffect(() => {
    if (session) {
      setSession(session);
    }
  }, [session, setSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSession();
    };
  }, [clearSession]);

  const handleSubmit = useCallback(async () => {
    const effectiveSessionId = resolvedSessionId || sessionId;
    console.log('[DocumentValidationPage] handleSubmit called:', {
      effectiveSessionId,
      resolvedSessionId,
      sessionId,
      isSubmitting,
      hasSelectedFields: !!selectedFields,
    });

    if (!effectiveSessionId || isSubmitting) {
      console.log(
        '[DocumentValidationPage] handleSubmit aborted - no sessionId or already submitting',
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // Submit validation
    try {
      console.log('[DocumentValidationPage] Calling submitValidationSession with:', {
        sessionId: effectiveSessionId,
        selectedFieldsCount: Object.keys(selectedFields || {}).length,
      });

      const result = await submitValidationMutation.mutateAsync({
        sessionId: effectiveSessionId,
        selectedFields: selectedFields as Record<string, unknown>,
      });

      console.log('[DocumentValidationPage] Submit result:', result);

      // Show success toast
      toast({
        variant: 'success',
        title: 'Validation saved successfully',
        description: 'The document data has been validated and saved.',
      });

      // Navigate to order details page if orderId is available, otherwise fallback to transaction documents
      // Pass a timestamp to force page refresh
      const refreshTimestamp = Date.now();
      if (stateOrderId) {
        navigate(`/orders/${stateOrderId}`, { state: { refresh: refreshTimestamp } });
      } else if (session?.transactionId) {
        navigate(`/cd/${session.transactionId}/documents`, {
          state: { refresh: refreshTimestamp },
        });
      }
    } catch (err) {
      console.error('[DocumentValidationPage] Submit error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit validation');
      setIsSubmitting(false);
    }
  }, [
    resolvedSessionId,
    sessionId,
    selectedFields,
    isSubmitting,
    session,
    navigate,
    setIsSubmitting,
    setError,
    submitValidationMutation,
  ]);

  const handleCancel = useCallback(() => {
    if (session?.transactionId) {
      navigate(`/cd/${session.transactionId}/documents`);
    } else if (transactionId) {
      navigate(`/cd/${transactionId}/documents`);
    }
  }, [session, transactionId, navigate]);

  // Polling state
  if (isPolling) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Waiting for document processing...</p>
          <p className="text-sm text-muted-foreground mt-2">This may take a few moments</p>
        </div>
      </div>
    );
  }

  // Polling error state
  if (pollError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <div className="text-destructive text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-semibold mb-2">Processing Error</h1>
          <p className="text-muted-foreground mb-4">{pollError}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Loading validation session
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading validation session...</p>
        </div>
      </div>
    );
  }

  // Error loading validation session
  if (error || !session) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <div className="text-destructive text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-semibold mb-2">Validation Session Error</h1>
          <p className="text-muted-foreground mb-4">
            {error?.message || 'Failed to load validation session'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      {/* Header */}
      <DocumentValidationHeader
        sessionId={session.sessionId}
        documentId={session.documentId}
        transactionId={session.transactionId}
        expiresAt={session.expiresAt}
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onSelectAll={selectAllFields}
        onDeselectAll={deselectAllFields}
      />

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {/* Validation Sections */}
      <div className="space-y-6">
        {/* Transaction Basics */}
        {session.extractedData && (
          <TransactionBasicsSection extractedData={session.extractedData} />
        )}

        {/* Loan Terms */}
        {session.extractedData && <LoanTermsSection extractedData={session.extractedData} />}

        {/* Line Items */}
        {session.extractedData?.lineItems && session.extractedData.lineItems.length > 0 && (
          <LineItemsSection
            extractedData={
              session.extractedData as {
                lineItems?: Array<{
                  section: string;
                  lineNumber: number;
                  lineNumberString: string;
                  description: string;
                  amount: number;
                  borrowerPaidAtClosing?: number;
                  borrowerPaidBeforeClosing?: number;
                  sellerPaidAtClosing?: number;
                  sellerPaidBeforeClosing?: number;
                  paidByOthers?: number;
                }>;
                totalLoanCosts?: number;
                totalClosingCosts?: number;
                borrowerPaidAtClosing?: number;
                borrowerPaidBeforeClosing?: number;
                sellerPaidAtClosing?: number;
                sellerPaidBeforeClosing?: number;
              }
            }
          />
        )}
      </div>

      {/* No data message */}
      {(!session.extractedData ||
        (!session.extractedData.propertyAddress &&
          !session.extractedData.loanAmount &&
          !session.extractedData.lineItems?.length)) && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No data was extracted from the document.</p>
          <p className="text-sm text-muted-foreground mt-2">
            Please try processing the document again or contact support.
          </p>
        </div>
      )}
    </div>
  );
}

export default DocumentValidationPage;
